import { BLUE_200, BLUE_900, GRAY_300, GRAY_600, GRAY_900, WHITE } from 'constants/styling/theme'

import { ConversationStatus } from 'models/conversations'
import { FC } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

interface Props {
  /** Currently active status tab. */
  value: ConversationStatus
  /** Count shown next to the "Ongoing" tab. */
  ongoingCount?: number
  /** Tab change handler. */
  onChange: (status: ConversationStatus) => void
}

const TABS: ConversationStatus[] = [ConversationStatus.ACTIVE, ConversationStatus.CLOSED]

const TAB_LABEL_KEY: Record<string, string> = {
  [ConversationStatus.ACTIVE]: 'chat:status.ongoing',
  [ConversationStatus.CLOSED]: 'chat:status.closed',
}

/** Ongoing / Closed segmented toggle for the conversation list (mirrors the Figma sidebar header). */
export const ConversationStatusToggle: FC<Props> = ({ value, ongoingCount, onChange }) => {
  const { t } = useTranslation(['chat'])

  return (
    <Stack
      direction="row"
      gap="0.4rem"
      padding="0.4rem"
      borderRadius="0.8rem"
      sx={{ backgroundColor: GRAY_300 }}
    >

      {TABS.map((tab) => {
        const isActive = tab === value
        const showCount = tab === ConversationStatus.ACTIVE && !!ongoingCount

        return (
          <Stack
            key={tab}
            direction="row"
            flex={1}
            gap="0.6rem"
            justifyContent="center"
            alignItems="center"
            onClick={() => onChange(tab)}
            sx={{
              cursor: 'pointer',
              padding: '0.6rem 1.2rem',
              borderRadius: '0.6rem',
              backgroundColor: isActive ? WHITE : 'transparent',
              boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
            }}
          >

            <Typography variant="text-sm" fontWeight={isActive ? 600 : 500} color={isActive ? GRAY_900 : GRAY_600}>
              {t(TAB_LABEL_KEY[tab])}
            </Typography>

            {showCount &&
              <Typography
                variant="text-xs"
                fontWeight={600}
                sx={{
                  color: BLUE_900,
                  backgroundColor: BLUE_200,
                  borderRadius: '1rem',
                  padding: '0 0.6rem',
                }}
              >
                {ongoingCount}
              </Typography>
            }

          </Stack>
        )
      })}

    </Stack>
  )
}
